import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { api, formatApiError, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo, { LogoMark } from "@/components/Logo";
import ProductsTab from "@/components/admin/ProductsTab";
import OrdersTab from "@/components/admin/OrdersTab";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back to the studio");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-parchment px-5" data-testid="admin-login-view">
      <form onSubmit={submit} className="w-full max-w-sm border border-line bg-white p-10" data-testid="admin-login-form">
        <LogoMark className="h-10 w-10 text-ink" />
        <h1 className="mt-6 font-display text-4xl tracking-tight">Studio panel</h1>
        <p className="mt-2 text-sm text-stone2">Only the owner gets past this door.</p>
        <div className="mt-8 space-y-5">
          <div>
            <label className="label-mono mb-1.5 block" htmlFor="admin-email">Email</label>
            <input id="admin-email" type="email" className="input-base" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" />
          </div>
          <div>
            <label className="label-mono mb-1.5 block" htmlFor="admin-password">Password</label>
            <input id="admin-password" type="password" className="input-base" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" />
          </div>
          {error && (
            <p className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button type="submit" disabled={busy} className="btn-solid w-full" data-testid="admin-login-submit">
            {busy ? "Checking…" : "Enter the studio"}
          </button>
          <Link to="/" className="block text-center text-xs font-semibold uppercase tracking-[0.15em] text-stone2 hover:text-terracotta" data-testid="admin-back-to-shop">
            Back to the shop
          </Link>
        </div>
      </form>
    </div>
  );
}

function Overview({ goTab }) {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
    api.get("/admin/orders").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const cards = [
    { label: "Products", value: stats ? stats.products : "…", testid: "stat-products" },
    { label: "Orders", value: stats ? stats.orders : "…", testid: "stat-orders" },
    { label: "New orders", value: stats ? stats.new_orders : "…", testid: "stat-new-orders" },
    { label: "Paid revenue", value: stats ? inr(stats.revenue) : "…", testid: "stat-revenue" },
  ];

  return (
    <div data-testid="admin-overview-tab">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-stat-cards">
        {cards.map((c) => (
          <div key={c.label} className="border border-line bg-white p-6" data-testid={c.testid}>
            <p className="label-mono">{c.label}</p>
            <p className="mt-3 font-display text-4xl">{c.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 border border-line bg-white">
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <p className="label-mono">Latest orders</p>
          <button onClick={() => goTab("orders")} className="text-xs font-semibold uppercase tracking-wide text-terracotta" data-testid="overview-view-all-orders">
            View all
          </button>
        </div>
        {recent.length === 0 ? (
          <p className="px-6 py-10 text-sm text-stone2">No orders yet — share the shop and they will land here.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent.map((o) => (
                <TableRow key={o.id}>
                  <TableCell className="font-mono text-xs font-semibold">{o.order_number}</TableCell>
                  <TableCell>{o.customer.name}</TableCell>
                  <TableCell>{inr(o.total)}</TableCell>
                  <TableCell className="capitalize">{o.order_status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");

  if (user === undefined) {
    return (
      <div className="grid min-h-screen place-items-center bg-parchment">
        <p className="label-mono">Checking session…</p>
      </div>
    );
  }

  if (!user) return <AdminLogin />;

  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "products", label: "Products" },
    { id: "orders", label: "Orders" },
  ];

  return (
    <div className="min-h-screen bg-parchment" data-testid="admin-dashboard">
      <header className="border-b border-line bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-8">
          <Link to="/" data-testid="admin-home-link">
            <Logo />
          </Link>
          <div className="flex items-center gap-4">
            <p className="hidden font-mono text-[11px] uppercase tracking-[0.15em] text-stone2 sm:block" data-testid="admin-user-email">
              {user.email}
            </p>
            <button onClick={logout} className="inline-flex items-center gap-2 border border-stone-300 px-4 py-2 text-xs font-semibold uppercase tracking-wide transition-colors hover:border-ink" data-testid="admin-logout-btn">
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-10 md:px-8">
        <div className="mb-8 flex gap-1 border-b border-line" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`-mb-px border-b-2 px-5 py-3 text-sm font-semibold transition-colors ${
                tab === t.id ? "border-terracotta text-ink" : "border-transparent text-stone2 hover:text-ink"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === "overview" && <Overview goTab={setTab} />}
        {tab === "products" && <ProductsTab />}
        {tab === "orders" && <OrdersTab />}
      </main>
    </div>
  );
}
